import type { LibraryPartRow, LibraryRowStatus } from './types'

/**
 * UID-405 (design-parity audit fix — audit findings (A)/(6)) — the design
 * handoff's `libStateCfg`/`libRow` (`Fictiv Platform.dc.html:7687-7694,
 * 8611-8668`) carries THREE readiness states this app's generated
 * `LibraryRowStatus` (`schema.gen.ts`, wire-frozen: `analyzing | ready |
 * needs_config | needs_quote`) has no counterpart for:
 *   - `needs-match` ("Find match" neutral CTA) — the design's OWN
 *     `filterStatus`-equivalent vocabulary already has `match` as a real,
 *     wire-carried `PartFilterStatus` value (`PART_STATUS_FILTERS`,
 *     types.ts) — F0/F2 previously folded it into `ready` (see the
 *     superseded comment this replaces below); this restores it as its
 *     own resolved state, no contract change needed since `filterStatus`
 *     already carries it.
 *   - `bundled` (disabled "Bundled" chip, `p.rfqLock`) and `quote_expired`
 *     ("Request quote" for a `ready` row whose quote lapsed, `p.ps===
 *     'quoted' && isQuoteExpired(p)`) have NO backend field on
 *     `GET /api/parts` today — flagged (not fabricated): `LibraryPartRow`
 *     gained two minimal, additive, OPTIONAL view-model fields for this
 *     (`rfqLock`, `quoteExpired` — see their doc comments on
 *     `LibraryPartRow`, types.ts) so the states are demonstrable in the UI
 *     ahead of the real backend field landing, same "additive, never
 *     forked" posture as `rowStatus`/`projects` before them.
 * `ResolvedRowStatus` is this screen's own superset VIEW-MODEL type — it
 * does NOT touch/widen the generated `LibraryRowStatus` union
 * (`schema.gen.ts` is generated from `openapi.yaml`; hand-editing it would
 * silently fork this app's types from the real wire contract, the same
 * "never hand-edit; change the contract, regen" rule this repo already
 * applies to every other generated artifact).
 */
export type ResolvedRowStatus = LibraryRowStatus | 'needs_match' | 'bundled' | 'quote_expired'

/**
 * UID-170/F2 (Story F0's `LibraryRowStatus`): reads the row's EXPLICIT
 * lifecycle state where present, and derives an equivalent for older/mocked
 * data that hasn't been upgraded to send `rowStatus` yet (F0's doc comment
 * gives this exact mapping — `match` folds into `ready`, since a
 * `match`-chip catalog part is still analyzed/orderable). Deleting the
 * fallback is safe once every `LibraryPartRow` producer always sends
 * `rowStatus`.
 *
 * Extracted from `PartsTable.tsx` (UID-257) so both the table's per-row
 * rendering AND the Library selection bar's gating (`allPriced` /
 * `anyNeedsConfig` below) read a row's lifecycle state through the SAME
 * resolver rather than each re-deriving it.
 *
 * UID-405 — precedence, matching the design's own `libRow` order
 * (`Fictiv Platform.dc.html:8611-8629`, `bundled` computed FIRST, then
 * `expired`, then the base state): a bundled row is `'bundled'`
 * regardless of its underlying status (it must be purchased as part of
 * that RFQ, full stop); an otherwise-`ready` row whose quote has expired
 * is `'quote_expired'`, not `'ready'`; `filterStatus === 'match'` (with no
 * explicit `rowStatus` override) now resolves to its own `'needs_match'`
 * rather than folding into `'ready'`.
 */
export function resolveRowStatus(item: LibraryPartRow): ResolvedRowStatus {
  if (item.rfqLock) return 'bundled'
  const base: LibraryRowStatus | 'needs_match' = item.rowStatus
    ? item.rowStatus
    : item.filterStatus === 'config'
      ? 'needs_config'
      : item.filterStatus === 'quote'
        ? 'needs_quote'
        : item.filterStatus === 'match'
          ? 'needs_match'
          : 'ready'
  if (base === 'ready' && item.quoteExpired) return 'quote_expired'
  return base
}

/**
 * UID-257 — the Library selection bar's "Add N to cart" is enabled only when
 * EVERY selected row is priced/orderable (`ready`), matching the design
 * handoff's `libStateCfg` cart-eligibility map (`cart:true` only for
 * priced/auto-configured/quoted; `cart:false` for needs-config/needs-quote).
 * An empty selection is NOT "all priced" (nothing to add).
 */
export function allPriced(rows: readonly LibraryPartRow[]): boolean {
  return rows.length > 0 && rows.every((row) => resolveRowStatus(row) === 'ready')
}

/**
 * UID-257 — the Library selection bar's "Request quote for N" is disabled
 * (with a tooltip) while ANY selected row still needs configuration (design
 * handoff §8: the bar's "Request quote for N" is "disabled/greyed with a
 * tooltip until configuration is resolved"). Configuration must be resolved
 * before a quote can be requested for the batch.
 */
export function anyNeedsConfig(rows: readonly LibraryPartRow[]): boolean {
  return rows.some((row) => resolveRowStatus(row) === 'needs_config')
}

/**
 * UID-3 — the Library selection bar's "Request quote for N" is now
 * conditionally RENDERED (not always shown-then-greyed): it appears only when
 * the selection contains at least one part that actually needs a quote,
 * matching the design's `libSelHasQuote` gate (`Fictiv Platform.dc.html
 * :10475`). A `quote_expired` row also needs a (re-)quote — it shares the same
 * RFQ affordance as `needs_quote` (see `PRIMARY_ACTION_LABEL`, PartsTable.tsx)
 * — so both count here.
 */
export function anyNeedsQuote(rows: readonly LibraryPartRow[]): boolean {
  return rows.some((row) => {
    const status = resolveRowStatus(row)
    return status === 'needs_quote' || status === 'quote_expired'
  })
}

/**
 * UID-418 — the SUBSET of a selection that actually needs configuration. Both
 * the "Configure N" CTA count AND the bulk-configure handler read this single
 * definition, so the count and the action can never disagree (the button
 * previously counted the whole selection while the modal configured the whole
 * selection; now both are scoped to exactly the needs-config rows). Mirrors
 * the `anyNeedsConfig` predicate above so the count, the show/hide gate, and
 * the action all read the same lifecycle state.
 */
export function selectNeedsConfig(rows: readonly LibraryPartRow[]): readonly LibraryPartRow[] {
  return rows.filter((row) => resolveRowStatus(row) === 'needs_config')
}

/**
 * UID-418 — the SUBSET of a selection needing a (re-)quote: `needs_quote` OR
 * `quote_expired`, exactly the rows `anyNeedsQuote` treats as quote-eligible.
 * Both the "Request quote for N" CTA count and the RFQ-queue handler read this,
 * so the button count matches what actually lands in the RFQ bundle.
 */
export function selectNeedsQuote(rows: readonly LibraryPartRow[]): readonly LibraryPartRow[] {
  return rows.filter((row) => {
    const status = resolveRowStatus(row)
    return status === 'needs_quote' || status === 'quote_expired'
  })
}

/** UID-418 — count of the `selectNeedsConfig` subset (the "Configure N" CTA). */
export function countNeedsConfig(rows: readonly LibraryPartRow[]): number {
  return selectNeedsConfig(rows).length
}

/** UID-418 — count of the `selectNeedsQuote` subset (the "Request quote for N" CTA). */
export function countNeedsQuote(rows: readonly LibraryPartRow[]): number {
  return selectNeedsQuote(rows).length
}

/**
 * UID-577 — whether a row's REQUIREMENTS cell shows its edit pencil.
 *
 * Mirrors the design's own `canEditReq` predicate verbatim (`Fictiv
 * Platform.dc.html:8614`):
 *
 *     canEditReq = (_phase!=='analyzing') && (p.ps!=='needs-config')
 *                                         && (p.ps!=='needs-match')
 *
 * The pencil is an EDIT affordance, so it only makes sense once there is a
 * configuration to edit. A `needs_config` / `needs_match` row has none — its
 * affordance is the primary-action cell's "Configure" / "Find match" CTA and
 * the readiness chip, which is why the demo page showed all three at once
 * (Slack, 2026-09-10: "here both are coming, and also the chip"). An
 * `analyzing` row is still resolving, so nothing is editable yet.
 *
 * Note the exclusions are on the RESOLVED status but deliberately do NOT
 * include `bundled` or `quote_expired`: the design computes `canEditReq`
 * from the row's BASE `p.ps`, so a bundled or lapsed-quote row whose
 * underlying state is otherwise editable keeps its pencil. Those two
 * override the row's ACTION, not whether its requirements can be edited.
 *
 * Callers still AND this with their own `onEditConfig` presence check — this
 * predicate answers "should this row have a pencil", not "is an edit handler
 * wired".
 */
export function canEditRequirements(item: LibraryPartRow): boolean {
  const status = resolveRowStatus(item)
  return status !== 'analyzing' && status !== 'needs_config' && status !== 'needs_match'
}
